// GERADO POR `pnpm gen:api` — NÃO EDITAR À MÃO.
// Fonte: specs/AIM-POC/contracts/api-openapi.yaml › `x-roles` de cada operação
// source-sha256: 62a3c73824312b40e9a285b64175f1eab1719c0db10c2b997d748696cbb633c3

import type { components, operations } from './types';

export type OperationId = keyof operations;
type Papel = components['schemas']['PapelUsuario'];

/**
 * Matriz papel × operação do FR-009, **gerada do contrato congelado** — a mesma
 * que o backend aplica por rota. Lista vazia = operação sem sessão (health, login,
 * fluxo do Google).
 *
 * `Record<OperationId, …>` é de propósito: operação nova sem papel aqui não
 * compila. Mudou o `x-roles` no contrato? `pnpm gen:api` — o teste de hash em
 * `api/__tests__/roles.test.ts` obriga.
 */
export const OPERATION_ROLES: Record<OperationId, readonly Papel[]> = {
  activatePolicy: ['admin', 'approver'],
  applyAdmissionConfig: ['admin'],
  approveApproval: ['admin', 'approver'],
  askAgent: ['admin', 'approver'],
  assignIdentity: ['admin', 'approver'],
  callAgentTool: ['admin', 'approver'],
  confirmResourceImport: ['admin'],
  createAdmissionToken: ['admin'],
  createEnvironment: ['admin'],
  createIdentityRole: ['admin', 'approver'],
  createPolicy: ['admin', 'approver'],
  createResource: ['admin'],
  createSite: ['admin'],
  createUser: ['admin'],
  deactivatePolicy: ['admin', 'approver'],
  deactivateUser: ['admin'],
  delegateAgentTool: ['admin', 'approver'],
  deleteChatEntry: ['admin'],
  deleteEnvironment: ['admin'],
  deleteIdentity: ['admin'],
  deleteIdentityRole: ['admin'],
  deletePolicy: ['admin', 'approver'],
  deleteResource: ['admin'],
  deleteResourceTool: ['admin'],
  deleteSite: ['admin'],
  deployIdentityImage: ['admin'],
  getAccessControl: ['admin', 'approver', 'viewer'],
  getAccessMap: ['admin', 'approver', 'viewer'],
  getApproval: ['admin', 'approver', 'viewer'],
  getApprovalsSummary: ['admin', 'approver', 'viewer'],
  getAuditEntry: ['admin', 'approver', 'viewer'],
  getChatDefaultAgent: ['admin', 'approver'],
  getChatEntry: ['admin', 'approver'],
  getContract: ['admin', 'approver', 'viewer'],
  getDecision: ['admin', 'approver', 'viewer'],
  getDiscoveryConfig: ['admin', 'approver', 'viewer'],
  getDiscoveryStatus: ['admin', 'approver', 'viewer'],
  getEnvironmentApprovalRules: ['admin', 'approver', 'viewer'],
  getHealth: [],
  getIdentity: ['admin', 'approver', 'viewer'],
  getIdentityCapabilities: ['admin', 'approver', 'viewer'],
  getIdentityContract: ['admin', 'approver', 'viewer'],
  getIdentityEnvironmentPreview: ['admin', 'approver', 'viewer'],
  getIdentityImpact: ['admin'],
  getIdentityRelease: ['admin', 'approver', 'viewer'],
  getIdentitySpiffe: ['admin', 'approver', 'viewer'],
  getIdentitySpiffePreview: ['admin', 'approver', 'viewer'],
  getInfraConfig: ['admin', 'approver', 'viewer'],
  getMe: ['admin', 'approver', 'viewer', 'pipeline'],
  getOverview: ['admin', 'approver', 'viewer'],
  getPoliciesSummary: ['admin', 'approver', 'viewer'],
  getPolicy: ['admin', 'approver', 'viewer'],
  getResource: ['admin', 'approver', 'viewer'],
  getResourceImpact: ['admin'],
  getResourcesSummary: ['admin', 'approver', 'viewer'],
  getRuntimeAggregate: ['admin', 'approver', 'viewer'],
  getRuntimeAuthorization: ['admin', 'approver', 'viewer'],
  getRuntimeStatus: ['admin', 'approver', 'viewer'],
  getShell: ['admin', 'approver', 'viewer'],
  getSiteAdmission: ['admin'],
  getTenant: ['admin', 'approver', 'viewer'],
  getUser: ['admin'],
  googleCallback: [],
  googleStart: [],
  ingestRelease: ['admin', 'pipeline'],
  listAdmissionVersions: ['admin'],
  listApprovals: ['admin', 'approver', 'viewer'],
  listAudit: ['admin', 'approver', 'viewer'],
  listDecisions: ['admin', 'approver', 'viewer'],
  listEnvironments: ['admin', 'approver', 'viewer'],
  listIdentities: ['admin', 'approver', 'viewer'],
  listIdentityContracts: ['admin', 'approver', 'viewer'],
  listIdentityPolicies: ['admin', 'approver', 'viewer'],
  listIdentityReleases: ['admin', 'approver', 'viewer'],
  listIdentityRoles: ['admin', 'approver', 'viewer'],
  listLabPeople: ['admin', 'approver'],
  listPolicies: ['admin', 'approver', 'viewer'],
  listResources: ['admin', 'approver', 'viewer'],
  listSiteAdmissions: ['admin'],
  listSites: ['admin', 'approver', 'viewer'],
  listUngovernedResources: ['admin'],
  listUsers: ['admin'],
  login: [],
  logout: ['admin', 'approver', 'viewer', 'pipeline'],
  machineToken: [],
  materializeAuthorization: ['admin', 'approver'],
  previewAdmissionConfig: ['admin'],
  previewResourceImport: ['admin'],
  reactivateUser: ['admin'],
  reconcileAdmission: ['admin', 'approver'],
  refreshAdmissionStatus: ['admin'],
  registerAdmission: ['admin'],
  reimportResource: ['admin'],
  rejectApproval: ['admin', 'approver'],
  relinkAgentResource: ['admin'],
  resetUserPassword: ['admin'],
  retryAdmissionConfig: ['admin'],
  retryAutoApproval: ['admin', 'approver'],
  revokeAdmissionToken: ['admin'],
  runDiscovery: ['admin', 'approver'],
  searchGlobal: ['admin', 'approver', 'viewer'],
  simulateCall: ['admin', 'approver', 'viewer'],
  startRuntimeReading: ['admin', 'approver', 'viewer'],
  submitAccessControlCandidate: ['admin', 'approver'],
  syncAgentResources: ['admin', 'approver'],
  testChatEntry: ['admin', 'approver'],
  testInfraConfigGroup: ['admin'],
  testSiteConnection: ['admin'],
  updateChatEntry: ['admin'],
  updateDiscoveryConfig: ['admin'],
  updateEnvironment: ['admin'],
  updateEnvironmentApprovalRules: ['admin'],
  updateIdentityRole: ['admin', 'approver'],
  updateInfraConfigGroup: ['admin'],
  updatePolicy: ['admin', 'approver'],
  updateResource: ['admin'],
  updateSite: ['admin'],
  updateTenant: ['admin'],
  updateUser: ['admin'],
  validateRego: ['admin', 'approver'],
};
